import React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import './App.css';
import './gameStages.css';
import './refinements.css';
import { publicDataFetch } from './api';
import TopBar from './components/TopBar';
import LandingScreen from './components/LandingScreen';
import TutorialScreen from './components/TutorialScreen';
import CombatStage from './components/ThousandPerformanceStage';
import CarnivalStage from './components/CarnivalStage';
import RevealScreen from './components/RevealScreen';
import FinalChallenge from './components/FinalChallenge';
import ResultScreen from './components/ResultScreen';
import {
  ROUND_ONE_SEQUENCE,
  createRoundTwoSequence,
  formatNcbDate,
  getDeviceType,
  getRank,
} from './game/gameConfig';

const EMPTY_STATS = { hits: 0, bestCombo: 0, score: 0 };

function App() {
  const [screen, setScreen] = useState('landing');
  const [muted, setMuted] = useState(() => localStorage.getItem('wc-muted') === 'true');
  const [playCount, setPlayCount] = useState(0);
  const [countLoading, setCountLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [trackingError, setTrackingError] = useState(false);
  const [roundOne, setRoundOne] = useState(EMPTY_STATS);
  const [roundTwo, setRoundTwo] = useState(EMPTY_STATS);
  const [result, setResult] = useState(null);
  const roundTwoSequence = useMemo(() => createRoundTwoSequence(), [screen]);

  const loadPlayCount = useCallback(async () => {
    setCountLoading(true);
    let total = 0;
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const response = await publicDataFetch(`read/play_sessions?page=${page}&limit=100`);
      if (response?.status !== 'success') {
        setCountLoading(false);
        return;
      }

      const rows = Array.isArray(response?.data) ? response.data : [];
      total += rows.length;
      hasMore = response?.metadata?.hasMore ?? rows.length === 100;
      page += 1;
    }

    setPlayCount(total);
    setCountLoading(false);
  }, []);

  useEffect(() => {
    loadPlayCount();
  }, [loadPlayCount]);

  const toggleMute = () => {
    setMuted((current) => {
      localStorage.setItem('wc-muted', String(!current));
      return !current;
    });
  };

  const startGame = async () => {
    if (starting) return;

    setStarting(true);
    setTrackingError(false);
    setScreen('tutorial');

    const response = await publicDataFetch('create/play_sessions', {
      method: 'POST',
      body: JSON.stringify({
        started_at: formatNcbDate(),
        device_type: getDeviceType(),
        entry_point: 'mpesport-wing-chun-103',
      }),
    });

    if (response?.status === 'success') {
      loadPlayCount();
    } else {
      setTrackingError(true);
    }

    setStarting(false);
  };

  const finishFinal = useCallback((finalHits) => {
    const hits = roundOne.hits + roundTwo.hits;
    const totalPrompts = ROUND_ONE_SEQUENCE.length + 12;
    const score = roundOne.score + roundTwo.score + finalHits * 35;
    const accuracy = Math.round((hits / totalPrompts) * 100);
    const finalResult = {
      accuracy,
      bestCombo: Math.max(roundOne.bestCombo, roundTwo.bestCombo),
      finalHits,
      score,
      title: getRank(score),
    };

    const previousBest = Number(localStorage.getItem('wc-best-score') || 0);
    localStorage.setItem('wc-best-score', String(Math.max(previousBest, score)));
    setResult(finalResult);
    setScreen('results');

    publicDataFetch('create/game_results', {
      method: 'POST',
      body: JSON.stringify({
        score,
        accuracy,
        best_combo: finalResult.bestCombo,
        title: finalResult.title,
        completed_at: formatNcbDate(),
      }),
    });
  }, [roundOne, roundTwo]);

  const restart = () => {
    setRoundOne(EMPTY_STATS);
    setRoundTwo(EMPTY_STATS);
    setResult(null);
    setTrackingError(false);
    setScreen('landing');
    loadPlayCount();
  };

  return (
    <div className="app-container">
      <div className={`app-shell ${muted ? 'is-muted' : ''}`}>
        <TopBar
          muted={muted}
          onToggleMute={toggleMute}
          playCount={playCount}
          countLoading={countLoading}
        />

        {screen === 'landing' && (
          <LandingScreen
            onStart={startGame}
            starting={starting}
            trackingError={trackingError}
          />
        )}

        {screen === 'tutorial' && (
          <TutorialScreen onComplete={() => setScreen('round-one')} />
        )}

        {screen === 'round-one' && (
          <CombatStage
            chapter="第一關 · 詠春千人匯演"
            title="千人同心守中線"
            description="跟隨千人匯演的整齊節奏，以最短動作守住中線。"
            sequence={ROUND_ONE_SEQUENCE}
            onComplete={(stats) => {
              setRoundOne(stats);
              setScreen('reveal-one');
            }}
          />
        )}

        {screen === 'reveal-one' && (
          <RevealScreen
            label="第一式 · 詠春千人匯演"
            title="一招一式，同心傳承"
            description="千人匯演以整齊步伐與共同節奏，呈現詠春文化跨世代、跨地域的凝聚力。"
            quote="守住中線，亦守住文化根源。"
            onContinue={() => setScreen('round-two')}
          />
        )}

        {screen === 'round-two' && (
          <CarnivalStage
            chapter="第二關 · 詠春嘉年華"
            title="感知與體驗"
            description="嘉年華內每一個互動體驗站，邀請你以反應感受詠春的節奏。"
            sequence={roundTwoSequence}
            onComplete={(stats) => {
              setRoundTwo(stats);
              setScreen('reveal-two');
            }}
          />
        )}

        {screen === 'reveal-two' && (
          <RevealScreen
            label="第二式 · 詠春嘉年華"
            title="不止觀看，更可親身參與"
            description="從互動體驗、文化展示到交流活動，讓不同年齡的參與者走近詠春。"
            quote="以武會友，以文化連結大灣區。"
            onContinue={() => setScreen('final')}
          />
        )}

        {screen === 'final' && (
          <FinalChallenge onComplete={finishFinal} />
        )}

        {screen === 'results' && result && (
          <ResultScreen
            result={result}
            bestScore={Number(localStorage.getItem('wc-best-score') || result.score)}
            onRestart={restart}
          />
        )}
      </div>
    </div>
  );
}

export default App;